import React from "react";
import { BarChart, Description, ErrorOutline, TrendingDown, TrendingUp } from "@material-ui/icons";
import {
  PieChart,
  Pie,
  Cell,
  BarChart as RechartsBarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import styles from "./dashboard.page.module.scss";

const DashboardPage = () => {
  const summaryCards = [
    {
      title: "Total Surveys",
      value: "8",
      trend: "+12% from last week",
      tone: "positive",
      icon: <BarChart />,
    },
    {
      title: "Avg Sentiment",
      value: "0.07",
      trend: "Neutral overall mood",
      tone: "warning",
      icon: <TrendingDown />,
    },
    {
      title: "Flagged Items",
      value: "3",
      trend: "Negative sentiment surveys",
      tone: "danger",
      icon: <ErrorOutline />,
    },
    {
      title: "Top Concern",
      value: "Poor road conditions",
      trend: "1 mentions",
      tone: "accent",
      icon: <span className={styles.rankBadge}>1</span>,
    },
  ];

  const sentimentDistribution = [
    { label: "Positive", value: 3, color: "#10b981" },
    { label: "Neutral", value: 1, color: "#f59e0b" },
    { label: "Negative", value: 3, color: "#ef4444" },
  ];

  const issueCategories = [
    { label: "Infrastructure", value: 232 },
    { label: "Public Safety", value: 189 },
    { label: "Education", value: 156 },
    { label: "Healthcare", value: 98 },
    { label: "Employment", value: 67 },
    { label: "Environment", value: 58 },
  ];

  const trendWeeks = [
    { week: "Week 1", date: "3/17", score: -0.65, surveys: 1 },
    { week: "Week 2", date: "3/24", score: 0.39, surveys: 2 },
    { week: "Week 3", date: "3/31", score: -0.48, surveys: 1 },
    { week: "Week 4", date: "4/7", score: 0.22, surveys: 4 },
  ];

  const topConcerns = [
    { label: "Poor road conditions and flooding", mentions: 145 },
    { label: "Lack of police presence and security", mentions: 127 },
    { label: "Overcrowded schools and classrooms", mentions: 98 },
    { label: "Traffic congestion", mentions: 87 },
    { label: "Water supply shortages", mentions: 76 },
    { label: "Healthcare accessibility", mentions: 65 },
    { label: "Job opportunities", mentions: 54 },
    { label: "Waste management", mentions: 43 },
  ];

  // const maxIssueValue = Math.max(...issueCategories.map((item) => item.value));
  const maxConcernMentions = Math.max(...topConcerns.map((item) => item.mentions));

  return (
    <div className={styles.dashboard}>
      <div className={styles.headerBlock}>
        <h1>Overview Dashboard</h1>
        <p>Real-time analytics and sentiment tracking</p>
      </div>

      <section className={styles.summaryGrid}>
        {summaryCards.map((card) => (
          <article key={card.title} className={styles.metricCard}>
            <div className={styles.metricTop}>
              <div>
                <p className={styles.metricTitle}>{card.title}</p>
                <p className={styles.metricValue}>{card.value}</p>
              </div>
              <div className={`${styles.iconWrap} ${styles[card.tone]}`}>{card.icon}</div>
            </div>
            <p className={`${styles.metricTrend} ${styles[card.tone]}`}>
              {card.tone === "positive" ? <TrendingUp fontSize="small" /> : null}
              {card.trend}
            </p>
          </article>
        ))}
      </section>

      <section className={`${styles.card} ${styles.summaryNarrative}`}>
        <h3>
          <Description fontSize="small" /> Executive Summary
        </h3>
        <p>
          <span>Overall Sentiment:</span> The electorate mood is slightly negative with an average
          sentiment score of 0.07. 3 out of 8 surveys (37.5%) reflect negative sentiment, primarily
          driven by infrastructure concerns and governance accountability issues.
        </p>
        <p>
          <span>Top Concerns:</span> The dominant concern is "Poor road conditions" with 1 mentions.
          Other major themes include poor road conditions, flooding, lack of police presence, and
          overcrowded schools. These infrastructure and public safety issues consistently dominate
          across different barangays.
        </p>
        <p>
          <span>Demographic Insights:</span> Family-first and community-oriented voters show
          stronger engagement depth, with detailed responses about local governance. First-time
          voters express more uncertainty in vote intent, while returning voters demonstrate
          definite preferences aligned with service delivery performance.
        </p>
        <p>
          <span>Government Ratings:</span> Local government performance receives mixed ratings, with
          mayors generally rated "Somewhat helpful" to "Not helpful" in areas with infrastructure
          challenges. Positive ratings correlate strongly with visible service improvements like new
          health centers and waste management programs.
        </p>
        <p>
          <span>Trend Analysis:</span> Sentiment has remained relatively stable over the past four
          weeks, with slight fluctuations based on campaign deployment locations. Rural and coastal
          areas show more positive sentiment when environmental and community programs are
          highlighted.
        </p>
      </section>

      <section className={styles.analyticsGrid}>
        <article className={styles.card}>
          <h3>Sentiment Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={sentimentDistribution}
                dataKey="value"
                nameKey="label"
                cx="50%"
                cy="50%"
                outerRadius={80}
                label
              >
                {sentimentDistribution.map((item, index) => (
                  <Cell key={`cell-${index}`} fill={item.color} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </article>

        <article className={styles.card}>
          <h3>Issues by Category</h3>
          <ResponsiveContainer width="100%" height={300}>
            <RechartsBarChart data={issueCategories}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" angle={-45} textAnchor="end" height={80} />
              <YAxis />
              <Tooltip />
              <Bar dataKey="value" fill="#3b82f6" />
            </RechartsBarChart>
          </ResponsiveContainer>
        </article>
      </section>

      <section className={styles.card} style={{ marginTop: "28px" }}>
        <h3>Sentiment Trend (Past 4 Weeks)</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={trendWeeks}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="week" />
            <YAxis domain={[-1, 1]} />
            <Tooltip
              formatter={(value) => value.toFixed(2)}
              labelFormatter={(label) => `Week: ${label}`}
            />
            <Legend />
            <Line
              type="monotone"
              dataKey="score"
              stroke="#3b82f6"
              dot={{ fill: "#3b82f6", r: 5 }}
              strokeWidth={2}
            />
          </LineChart>
        </ResponsiveContainer>
      </section>

      <section className={styles.card} style={{ marginTop: "28px" }}>
        <h3>Top Concerns (Verbatim)</h3>
        <div className={styles.concernsList}>
          {topConcerns.map((item, index) => (
            <div key={item.label} className={styles.concernRow}>
              <div className={styles.concernLeft}>
                <span className={styles.rankBadge}>{index + 1}</span>
                <span>{item.label}</span>
              </div>
              <div className={styles.concernRight}>
                <div className={styles.concernTrack}>
                  <div
                    className={styles.concernFill}
                    style={{ width: `${(item.mentions / maxConcernMentions) * 100}%` }}
                  />
                </div>
                <strong>{item.mentions}</strong>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default DashboardPage;
